<template>
  <InputText
    :class="classComputed"
    :fluid="props.fluid"
    :id="fieldIdComputed"
    :pt="ptComputed"
    :required="required"
    :type="props.type"
    @update:modelValue="onChange"
    @keyup="onKeyup"
  />
</template>

<script setup lang="ts">
import type { IValidatedForm } from "@common/hooks";
import type { InputTextTypeType } from "@common/types";
import type { PassThrough } from "@primevue/core";
import type { InputTextPassThroughOptions } from "primevue/inputtext";
import InputText from "primevue/inputtext";
import type { PropType } from "vue";
import { computed, inject, useId } from "vue";

// Props
const props = defineProps({
  autoFocus: {
    type: Boolean,
    required: false,
  },
  fluid: {
    type: Boolean,
    required: false,
  },
  id: {
    type: String,
    required: false,
  },
  pt: {
    type: Object as PropType<PassThrough<InputTextPassThroughOptions>>,
    required: false,
  },
  required: {
    type: Boolean,
    required: false,
  },
  type: {
    type: String as PropType<InputTextTypeType>,
    required: false,
    default: "text",
  },
});

// Emits
const emit = defineEmits<{
  (e: "change", value: string | undefined): void;
  (e: "enter"): void;
  (e: "keyup", event: KeyboardEvent): void;
}>();

// Variables
const defaultId = useId();
const fieldId = inject<string | null>("$id", null);
const isError = inject<boolean | null>("$isError", false);
const target = inject<string | null>("$target", null);
const validatedForm = inject<IValidatedForm | null>("$validatedForm", null);

// Computed
const classComputed = computed(() => {
  const result = [];

  if (isError || (target && validatedForm?.validator.value.errors[target])) {
    result.push("p-invalid");
  }

  return result;
});

const fieldIdComputed = computed(() => {
  return props.id ?? fieldId ?? defaultId;
});

const ptComputed = computed(() => {
  const result: any = props.pt ?? {};

  if (props.autoFocus) {
    result.root = result.root ?? {};
    result.root.autoFocus = true;
  }

  return result;
});

// Methods
const onChange = (value: string | undefined) => {
  validateField();

  emit("change", value);
};

const onKeyup = (e: KeyboardEvent) => {
  validateField();

  emit("keyup", e);

  if (e.key === "Enter") {
    emit("enter");
  }
};

const validateField = () => {
  if (target && validatedForm) {
    validatedForm.validateField(target);
  }
};
</script>
